import { useState, useRef, useCallback } from "react";

interface PinchZoomState {
  scale: number;
  translateX: number;
  translateY: number;
}

interface UsePinchZoomReturn {
  state: PinchZoomState;
  handlers: {
    onTouchStart: (e: React.TouchEvent) => void;
    onTouchMove: (e: React.TouchEvent) => void;
    onTouchEnd: () => void;
  };
  reset: () => void;
  style: React.CSSProperties;
}

export function usePinchZoom(minScale = 1, maxScale = 4): UsePinchZoomReturn {
  const [state, setState] = useState<PinchZoomState>({
    scale: 1,
    translateX: 0,
    translateY: 0,
  });

  const initialDistance = useRef<number | null>(null);
  const initialScale = useRef(1);
  const lastTap = useRef(0);
  const initialTouch = useRef<{ x: number; y: number } | null>(null);
  const initialTranslate = useRef({ x: 0, y: 0 });

  const getDistance = (e: React.TouchEvent) => {
    const touch0 = e.touches[0];
    const touch1 = e.touches[1];
    if (!touch0 || !touch1) return 0;
    const dx = touch0.clientX - touch1.clientX;
    const dy = touch0.clientY - touch1.clientY;
    return Math.sqrt(dx * dx + dy * dy);
  };

  const reset = useCallback(() => {
    setState({ scale: 1, translateX: 0, translateY: 0 });
  }, []);

  const onTouchStart = useCallback((e: React.TouchEvent) => {
    const touch0 = e.touches[0];
    if (!touch0) return;

    // Double tap to reset
    const now = Date.now();
    if (now - lastTap.current < 300 && e.touches.length === 1) {
      e.preventDefault();
      reset();
      lastTap.current = 0;
      return;
    }
    lastTap.current = now;

    if (e.touches.length === 2) {
      e.preventDefault();
      initialDistance.current = getDistance(e);
      initialScale.current = state.scale;
    } else if (e.touches.length === 1 && state.scale > 1) {
      // Pan when zoomed
      initialTouch.current = { x: touch0.clientX, y: touch0.clientY };
      initialTranslate.current = { x: state.translateX, y: state.translateY };
    }
  }, [state.scale, state.translateX, state.translateY, reset]);

  const onTouchMove = useCallback((e: React.TouchEvent) => {
    const touch0 = e.touches[0];
    if (!touch0) return;

    if (e.touches.length === 2 && initialDistance.current !== null) {
      e.preventDefault();
      const currentDistance = getDistance(e);
      const scaleChange = currentDistance / initialDistance.current;
      const newScale = Math.min(maxScale, Math.max(minScale, initialScale.current * scaleChange));

      setState(prev => ({
        ...prev,
        scale: newScale,
        // Reset translate if zooming back to 1
        translateX: newScale <= 1 ? 0 : prev.translateX,
        translateY: newScale <= 1 ? 0 : prev.translateY,
      }));
    } else if (e.touches.length === 1 && initialTouch.current && state.scale > 1) {
      // Pan
      const dx = touch0.clientX - initialTouch.current.x;
      const dy = touch0.clientY - initialTouch.current.y;

      // Limit panning based on scale
      const maxPan = (state.scale - 1) * 150;
      const newX = Math.min(maxPan, Math.max(-maxPan, initialTranslate.current.x + dx));
      const newY = Math.min(maxPan, Math.max(-maxPan, initialTranslate.current.y + dy));

      setState(prev => ({
        ...prev,
        translateX: newX,
        translateY: newY,
      }));
    }
  }, [state.scale, minScale, maxScale]);

  const onTouchEnd = useCallback(() => {
    initialDistance.current = null;
    initialTouch.current = null;

    // Snap back to 1 if close
    if (state.scale < 1.1) {
      reset();
    }
  }, [state.scale, reset]);

  const style: React.CSSProperties = {
    transform: `translate(${state.translateX}px, ${state.translateY}px) scale(${state.scale})`,
    transition: initialDistance.current === null ? 'transform 0.1s ease-out' : 'none',
    touchAction: 'none',
  };

  return {
    state,
    handlers: {
      onTouchStart,
      onTouchMove,
      onTouchEnd,
    },
    reset,
    style,
  };
}
